/**
 * Types for post generation with modern slot-based entity system
 */
import { MediaType } from '../../core/template.schemas';

/**
 * Nested map of manual overrides: { [slotKey]: { [attributePath]: value } }
 */
export interface AttributeOverrideMap {
  [slotKey: string]: {
    [attributePath: string]: any;
  };
}

/**
 * Slot-based entity selection map
 */
export interface EntitySlotSelectionMap {
  [slotKey: string]: EntitySlotSelection;
}

/**
 * Individual entity slot selection
 */
export interface EntitySlotSelection {
  /** Unique slot identifier (e.g. "Player-scorer", "Team-home") */
  slotKey: string;
  /** Entity type (e.g. "Player", "Team") */
  entityType: string;
  /** Slot name (e.g. "scorer", "home") or null for default */
  slotName: string | null;
  /** User-friendly label for the slot */
  displayLabel: string;
  /** Selected entity instance */
  selectedInstance: EntityInstance | null;
  /** Whether this slot is required */
  required: boolean;
  /** Binding IDs that use this slot */
  bindingIds: string[];
  /** Template slot ID reference */
  slotId?: string;
  /** Attribute paths used by this slot */
  attributePaths?: string[];
  /** Missing required attributes that need manual input */
  missingAttributes?: { attributePath: string; mediaType: MediaType }[];
}

/**
 * Entity instance representation
 */
export interface EntityInstance {
  /** Unique entity ID */
  id: string;
  /** Display label for the entity */
  label: string;
  /** Entity data payload */
  data: any;
  /** Entity type */
  type: string;
}

/**
 * Selector-based value selection map
 */
export interface SelectorSelectionMap {
  [selectorId: string]: SelectorSelection;
}

/**
 * Individual selector value selection
 */
export interface SelectorSelection {
  /** Unique selector identifier */
  selectorId: string;
  /** User-friendly display label */
  displayLabel: string;
  /** Media type for this selector */
  mediaType: MediaType;
  /** Selected value for this selector */
  selectedValue: any;
  /** Whether this selector is required */
  required: boolean;
  /** Binding IDs that reference this selector */
  bindingIds: string[];
  /** Optional format applied on resolution */
  format?: any;
  /** Optional default value from template */
  defaultValue?: any;
}

/**
 * Post generation state management
 */
export interface PostGenerationState {
  /** Current step in generation process */
  currentStep: 'analysis' | 'selection' | 'preview' | 'generation';
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Error during generation */
  error?: string;
}

/**
 * Utility functions for slot management
 */
export class EntitySlotUtils {
  /**
   * Generates unique slot key from entity type and slot name
   */
  static generateSlotKey(entityType: string, slotName: string | null): string {
    return slotName ? `${entityType}-${slotName}` : `${entityType}-default`;
  }

  /**
   * Parses slot key into entity type and slot name
   */
  static parseSlotKey(slotKey: string): { entityType: string; slotName: string | null } {
    const parts = slotKey.split('-');
    if (parts.length === 2 && parts[1] === 'default') {
      return { entityType: parts[0], slotName: null };
    }
    if (parts.length >= 2) {
      const entityType = parts[0];
      const slotName = parts.slice(1).join('-');
      return { entityType, slotName };
    }
    return { entityType: slotKey, slotName: null };
  }

  /**
   * Checks if all required slots have selected instances
   */
  static areRequiredSlotsSelected(slots: EntitySlotSelectionMap): boolean {
    return Object.values(slots).every(slot => {
      if (!slot.required) return true;
      return slot.selectedInstance !== null;
    });
  }

  /**
   * Gets all selected entity instances
   */
  static getSelectedInstances(slots: EntitySlotSelectionMap): EntityInstance[] {
    return Object.values(slots)
      .map(slot => slot.selectedInstance)
      .filter((instance): instance is EntityInstance => instance !== null);
  }

  /**
   * Gets slots filtered by entity type
   */
  static getSlotsByEntityType(slots: EntitySlotSelectionMap, entityType: string): EntitySlotSelection[] {
    return Object.values(slots).filter(slot => slot.entityType === entityType);
  }

  /**
   * Creates empty slot map
   */
  static createEmptySlotMap(): EntitySlotSelectionMap {
    return {};
  }

  /**
   * Creates deep clone of slot map
   */
  static cloneSlotMap(slots: EntitySlotSelectionMap): EntitySlotSelectionMap {
    const cloned: EntitySlotSelectionMap = {};
    Object.entries(slots).forEach(([key, slot]) => {
      cloned[key] = {
        ...slot,
        selectedInstance: slot.selectedInstance ? { ...slot.selectedInstance } : null
      };
    });
    return cloned;
  }

  /**
   * Gets completion progress for slots
   */
  static getCompletionProgress(slots: EntitySlotSelectionMap): {
    completed: number;
    total: number;
    percentage: number;
  } {
    const allSlots = Object.values(slots);
    const completed = allSlots.filter(slot => slot.selectedInstance !== null).length;
    const total = allSlots.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  }

  /**
   * Validates slot selection completeness
   */
  static validateSlotSelection(slots: EntitySlotSelectionMap): {
    isValid: boolean;
    missingRequired: EntitySlotSelection[];
    errors: string[];
  } {
    const missingRequired = Object.values(slots).filter(
      slot => slot.required && !slot.selectedInstance
    );

    const errors: string[] = [];
    if (missingRequired.length > 0) {
      errors.push(`Missing required slots: ${missingRequired.map(s => s.displayLabel).join(', ')}`);
    }

    return {
      isValid: missingRequired.length === 0,
      missingRequired,
      errors
    };
  }
}